import { useState } from "react";
import { Plus, Sparkles, Edit, Package, FolderOpen } from "lucide-react";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../ui/collapsible";
import { SortableItem, DragHandle } from "./SortableItem";
import { AdminCategoryCard } from "./AdminCategoryCard";
import { CateringMenu, CateringMenuCategory, CateringMenuItem, CateringUpgrade, CateringAddon, CateringCategoryGroup, organizeByGroups } from "../../../../hooks/useCateringMenus";
import { EditCategoryDialog } from "./EditCategoryDialog";
import { EditCategoryGroupDialog } from "./EditCategoryGroupDialog";
import { EditMenuItemDialog } from "./EditMenuItemDialog";
import { EditMenuDialog } from "./EditMenuDialog";
import { EditUpgradeDialog } from "./EditUpgradeDialog";
import { EditAddonDialog } from "./EditAddonDialog";

interface EditCategoryProps {
  menuCategory: CateringMenuCategory[];
  menuUpgrades: CateringUpgrade[];
  menuAddons: CateringAddon[];
  productGroup: CateringCategoryGroup[];
  saveCategory: (categroy: any, type: string, updateIndex?: string) => void;
  saveProduct: (product: any, type: string, categoryID: string, updateID?: string) => void;
  saveUpgrad: (upgrade: any, type: string, updateID?: string) => void;
  saveAddons: (addons: any, type: string, updateID?: string) => void;
  saveGroup: (group: any, type: string, updateID?: string) => void;
}

export default function AdminCateringMenus({ menuCategory, menuUpgrades, menuAddons, productGroup, saveCategory, saveProduct, saveUpgrad, saveAddons, saveGroup }: EditCategoryProps) {
  const [editingCategory, setEditingCategory] = useState<CateringMenuCategory | null>(null);
  const [editingItem, setEditingItem] = useState<CateringMenuItem | null>(null);
  const [editingMenu, setEditingMenu] = useState<CateringMenu | null>(null);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [createCategoryMenuId, setCreateCategoryMenuId] = useState<string | null>(null);
  const [createItemCategoryId, setCreateItemCategoryId] = useState<string | null>(null);

  const [addCategory, setAddCategory] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  // Upgrade dialog state
  const [editingUpgrade, setEditingUpgrade] = useState<CateringUpgrade | null>(null);
  const [createUpgrade, setCreateUpgrade] = useState(false);

  // Addon dialog state
  const [editingAddon, setEditingAddon] = useState<CateringAddon | null>(null);
  const [showCreateAddon, setShowCreateAddon] = useState(false);

  // Category Group dialog state
  const [editingGroup, setEditingGroup] = useState<CateringCategoryGroup | null>(null);
  const [createGroupMenuId, setCreateGroupMenuId] = useState<string | null>(null);
  const [addCategoryGroup, setAddCategoryGroup] = useState(false);

  const handleCategoryDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const cats = menuCategory;
    const oldIndex = cats.findIndex((c) => c.id === active.id);
    const newIndex = cats.findIndex((c) => c.id === over.id);

    const [moved] = cats.splice(oldIndex, 1);
    cats.splice(newIndex, 0, moved);
    let updatedMenus = cats.map((m, index: number) => ({ ...m, display_order: index + 1 }));
    saveCategory(updatedMenus, "sort");
  };
  const refetch = () => {};
  const handleUpgradeDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const upgrades = menuUpgrades || [];
    const oldIndex = upgrades.findIndex((u) => u.id === active.id);
    const newIndex = upgrades.findIndex((u) => u.id === over.id);

    const newUpgrades = [...upgrades];
    const [moved] = newUpgrades.splice(oldIndex, 1);
    newUpgrades.splice(newIndex, 0, moved);
    saveUpgrad(newUpgrades, "sort");
  };

  const handleAddonDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = menuAddons.findIndex((a) => a.id === active.id);
    const newIndex = menuAddons.findIndex((a) => a.id === over.id);

    const newAddons = [...menuAddons];
    const [moved] = newAddons.splice(oldIndex, 1);
    newAddons.splice(newIndex, 0, moved);
    saveAddons(newAddons, "sort");
  };

  // Get the menu that's currently being edited for category (to pass groups)
  /* const getGroupsForCategoryDialog = (): CateringCategoryGroup[] => {
    if (editingCategory) {
      // Find the menu that owns this category
      const ownerMenu = menus.find(m => m.categories?.some(c => c.id === editingCategory.id));
      return ownerMenu?.category_groups || [];
    }
    if (createCategoryMenuId) {
      const menu = menus.find(m => m.id === createCategoryMenuId);
      return menu?.category_groups || [];
    }
    return productGroup;
  };
 */
  const renderCategoriesSection = (categories: CateringMenuCategory[], groups: CateringCategoryGroup[]) => {
    console.log(categories, groups, "menumenumenumenumenu");
    const organized = organizeByGroups(categories, groups);
    console.log(organized, "organizedorganizedorganized");

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Categories ({categories.length})</h3>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setAddCategoryGroup(true)}>
              <FolderOpen className="h-3 w-3" />
              Add Group
            </Button>
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setAddCategory(true)}>
              <Plus className="h-3 w-3" />
              Add Category
            </Button>
          </div>
        </div>

        {/* Grouped categories */}
        {organized.groups.map(({ group, categories: groupCats }) => (
          <div key={group.id} className="border rounded-lg p-3 bg-primary/5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-primary" />
                <span className="font-semibold text-sm">{group.name}</span>
                <Badge variant="outline" className="text-xs">
                  {groupCats.length} subcategories
                </Badge>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditingGroup(group)}>
                <Edit className="h-3 w-3" />
              </Button>
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleCategoryDragEnd(e)}>
              <SortableContext items={groupCats.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3 pl-2">
                  {groupCats.map((category) => (
                    <SortableItem key={category.id} id={category.id}>
                      <AdminCategoryCard
                        category={category}
                        onEditCategory={() => setEditingCategory(category)}
                        onEditItem={(item) => setEditingItem(item)}
                        onAddItem={(categoryId) => setCreateItemCategoryId(categoryId)}
                        onChangeSort={() => {}}
                        onRefetch={refetch}
                      />
                    </SortableItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        ))}

        {/* Ungrouped categories */}
        {organized.ungrouped.length > 0 && (
          <>
            {organized.groups.length > 0 && (
              <div className="flex items-center gap-2 mt-4">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Ungrouped Categories</span>
              </div>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleCategoryDragEnd(e)}>
              <SortableContext items={organized.ungrouped.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {organized.ungrouped.map((cat) => (
                    <SortableItem key={cat.id} id={cat.id}>
                      <AdminCategoryCard
                        category={cat}
                        onEditCategory={() => setEditingCategory(cat)}
                        onEditItem={(item) => {
                          setEditingItem(item);
                          setCreateItemCategoryId(cat.id);
                        }}
                        onAddItem={(categoryId) => setCreateItemCategoryId(categoryId)}
                        onChangeSort={(product, type, categoryID) => saveProduct(product, type, categoryID)}
                        onRefetch={refetch}
                      />
                    </SortableItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </>
        )}

        {categories.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No categories yet. Add one to get started.</p>}
      </div>
    );
  };

  const upgrades = menuUpgrades || [];
  console.log(addCategoryGroup, productGroup, "addCategoryaddCategoryaddCategory");
  return (
    <div className="min-h-screen bg-background">
      <div className="">
        <div className="mt-4 space-y-6">
          {/* Categories Section with groups */}
          {renderCategoriesSection(menuCategory, productGroup)}
        </div>

        {/* Upgrades Section */}
        <div className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Upgrades ({upgrades.length})
            </h3>
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setCreateUpgrade(true)}>
              <Plus className="h-3 w-3" />
              Add Upgrade
            </Button>
          </div>

          {upgrades.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleUpgradeDragEnd(e)}>
              <SortableContext items={upgrades.map((u) => u.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {upgrades.map((upgrade) => (
                    <SortableItem key={upgrade.id} id={upgrade.id}>
                      <div className="flex items-center justify-between py-2 px-3 bg-muted/30 border rounded-md">
                        <div className="flex items-center gap-3">
                          <DragHandle />
                          {upgrade.image_url && <img src={upgrade.image_url} alt={upgrade.name} className="w-8 h-8 rounded object-cover" />}
                          <div>
                            <span className="text-sm font-medium">{upgrade.name}</span>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {upgrade.category}
                              </Badge>
                              {upgrade.price_per_pax && upgrade.price_per_pax > 0 && <span className="text-xs text-accent">+${upgrade.price_per_pax.toFixed(2)}/pax</span>}
                              {upgrade.flat_price && upgrade.flat_price > 0 && <span className="text-xs text-accent">+${upgrade.flat_price.toFixed(2)} flat</span>}
                            </div>
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setEditingUpgrade(upgrade)}>
                          <Edit className="h-3 w-3" />
                        </Button>
                      </div>
                    </SortableItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <p className="text-sm text-muted-foreground py-3 text-center">No upgrades yet.</p>
          )}
        </div>

        {/* Add-ons Section */}
        <div className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Package className="h-4 w-4" />
              Add-ons ({menuAddons.length})
            </h3>
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setShowCreateAddon(true)}>
              <Plus className="h-3 w-3" />
              Add Add-on
            </Button>
          </div>

          {menuAddons.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleAddonDragEnd}>
              <SortableContext items={menuAddons.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {menuAddons.map((addon) => (
                    <SortableItem key={addon.id} id={addon.id}>
                      <div className="flex items-center justify-between py-2 px-3 bg-muted/30 border rounded-md">
                        <div className="flex items-center gap-3">
                          <DragHandle />
                          {addon.image_url && <img src={addon.image_url} alt={addon.name} className="w-8 h-8 rounded object-cover" />}
                          <div>
                            <span className="text-sm font-medium">{addon.name}</span>
                            <div className="flex items-center gap-2">
                              {addon.price_per_pax && addon.price_per_pax > 0 && <span className="text-xs text-accent">+${addon.price_per_pax.toFixed(2)}/pax</span>}
                              {addon.flat_price && addon.flat_price > 0 && <span className="text-xs text-accent">+${addon.flat_price.toFixed(2)} flat</span>}
                            </div>
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setEditingAddon(addon)}>
                          <Edit className="h-3 w-3" />
                        </Button>
                      </div>
                    </SortableItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <p className="text-sm text-muted-foreground py-3 text-center">No add-ons yet.</p>
          )}
        </div>
      </div>

      {/* Edit Dialogs */}
      <EditMenuDialog
        menu={showCreateMenu ? null : editingMenu}
        open={showCreateMenu || !!editingMenu}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateMenu(false);
            setEditingMenu(null);
          }
        }}
        onSave={refetch}
      />

      <EditCategoryGroupDialog
        group={editingGroup}
        open={!!editingGroup || !!createGroupMenuId || addCategoryGroup}
        onOpenChange={(open) => {
          if (!open) {
            setEditingGroup(null);
            setCreateGroupMenuId(null);
          }
        }}
        onSave={(group, type, updateID) => {
          saveGroup(group, type, updateID);
          setAddCategoryGroup(false);
          setEditingGroup(null);
          setCreateGroupMenuId(null);
        }}
      />

      <EditCategoryDialog
        category={editingCategory}
        open={!!editingCategory || !!createCategoryMenuId || addCategory}
        onOpenChange={(open) => {
          if (!open) {
            setEditingCategory(null);
            setCreateCategoryMenuId(null);
          }
        }}
        onSave={(categroy, type, updateID) => {
          saveCategory(categroy, type, updateID);
          setAddCategory(false);
          setEditingCategory(null);
          setCreateCategoryMenuId(null);
        }}
        availableGroups={productGroup}
      />

      <EditMenuItemDialog
        item={editingItem}
        categoryId={createItemCategoryId || undefined}
        open={!!editingItem || !!createItemCategoryId}
        onOpenChange={(open) => {
          if (!open) {
            setEditingItem(null);
            setCreateItemCategoryId(null);
          }
        }}
        onSave={(product, type, categoryID, updateID) => {
          saveProduct(product, type, categoryID, updateID);
          setEditingItem(null);
          setCreateItemCategoryId(null);
        }}
      />

      <EditUpgradeDialog
        upgrade={editingUpgrade}
        open={!!editingUpgrade || createUpgrade}
        onOpenChange={(open) => {
          if (!open) {
            setEditingUpgrade(null);
            setCreateUpgrade(false);
          }
        }}
        onSave={(upgrade, type, updateID) => {
          saveUpgrad(upgrade, type, updateID);
        }}
      />

      <EditAddonDialog
        addon={showCreateAddon ? null : editingAddon}
        open={showCreateAddon || !!editingAddon}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateAddon(false);
            setEditingAddon(null);
          }
        }}
        onSave={(addons, type, updateID) => {
          saveAddons(addons, type, updateID);
        }}
      />
    </div>
  );
}
